import { Navigate, NavLink, useParams } from 'react-router';
import { Alert, Card, Spinner } from '../../components/ui';
import { t } from '../../i18n';
import { useMe } from '../../lib/auth';
import { errorMessage } from '../../lib/errors';
import { can } from '../../lib/permissions';
import { moduleName } from '../access/data';
import { CalendarSection } from './CalendarSection';
import { CatalogSection } from './CatalogSection';
import { useCatalogs } from './data';
import { HolidaysSection } from './HolidaysSection';
import { SettingsSection } from './SettingsSection';

const link = ({ isActive }: { isActive: boolean }) => `block rounded px-3 py-2 text-sm ${isActive ? 'bg-slate-100 font-medium text-slate-900' : 'text-slate-700 hover:bg-slate-50'}`;

/**
 * Panel de Parametrización (guía 7.11): un solo panel para toda la plataforma. A la izquierda, los parámetros
 * generales y los catálogos que el usuario puede administrar (la API los informa con sus campos).
 */
export function ParametersPage({ section }: { section: 'generales' | 'calendario' | 'feriados' | 'catalogo' | 'inicio' }) {
  const me = useMe();
  const catalogs = useCatalogs();
  const { catalog: catalogKey } = useParams();
  const canSettings = can(me, 'plataforma.parametros.gestionar');

  if (!catalogs.data) return catalogs.error ? <Alert>{errorMessage(catalogs.error)}</Alert> : <Spinner label={t('comun.cargando')} />;
  if (!canSettings && catalogs.data.length === 0) return <p className="text-sm text-slate-600">{t('acceso.sin_permiso')}</p>;
  if (section === 'inicio') {
    return <Navigate to={canSettings ? '/parametros/generales' : `/parametros/catalogos/${catalogs.data[0]!.key}`} replace />;
  }

  const catalog = section === 'catalogo' ? catalogs.data.find((c) => c.key === catalogKey) : undefined;
  const modules = [...new Set(catalogs.data.map((c) => c.module))];

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold text-tinta">{t('param.titulo')}</h1>
        <p className="max-w-2xl text-sm text-slate-600">{t('param.descripcion')}</p>
      </div>
      <div className="grid gap-4 lg:grid-cols-[16rem_1fr]">
        <Card className="self-start p-2">
          <nav aria-label={t('param.titulo')} className="space-y-3">
            {canSettings && (
              <div>
                <NavLink to="/parametros/generales" className={link}>
                  {t('param.generales')}
                </NavLink>
                <NavLink to="/parametros/calendario" className={link}>
                  {t('cal.titulo')}
                </NavLink>
                <NavLink to="/parametros/feriados" className={link}>
                  {t('fer.titulo')}
                </NavLink>
              </div>
            )}
            {modules.map((m) => (
              <div key={m}>
                <p className="px-3 pb-1 text-xs font-semibold text-slate-500">
                  {t('param.catalogos')} · {moduleName(m)}
                </p>
                {catalogs.data
                  .filter((c) => c.module === m)
                  .map((c) => (
                    <NavLink key={c.key} to={`/parametros/catalogos/${c.key}`} className={link}>
                      {c.title}
                    </NavLink>
                  ))}
              </div>
            ))}
          </nav>
        </Card>
        <div>
          {['generales', 'calendario', 'feriados'].includes(section) && !canSettings && <p className="text-sm text-slate-600">{t('acceso.sin_permiso')}</p>}
          {section === 'generales' && canSettings && <SettingsSection />}
          {section === 'calendario' && canSettings && <CalendarSection />}
          {section === 'feriados' && canSettings && <HolidaysSection />}
          {section === 'catalogo' && (catalog ? <CatalogSection key={catalog.key} catalog={catalog} /> : <p className="text-sm text-slate-600">{t('error.no_encontrado')}</p>)}
        </div>
      </div>
    </div>
  );
}
